// EPI Job 详情页 Service Requests 子表的自定义行为
// 对应 CompanyWebUIConfig.xml 中 <section name="ServiceRequest"> 的行按钮
import React from 'react';
import ReactDOM from 'react-dom';
import { message } from 'antd';
import { httpClient } from 'util/Api';
import { config } from 'util/config';
import * as globalFunction from 'util/GlobalFunction';
import { operationalFunction, sendEmail as openEmail } from 'components/UIBuilder/SectionFunction/Common';
import ServiceRequestNoteModal from 'routes/Business/Job/JobDetail/ServiceRequestNoteModal';
import ServiceRequestUploadDocumentModal from 'routes/Business/Job/JobDetail/ServiceRequestUploadDocumentModal';
import ServiceRequestChangeStatusModal from 'routes/Business/Job/JobDetail/ServiceRequestChangeStatusModal';
import ServiceRequestRecycleBinModal from 'routes/Business/Job/JobDetail/ServiceRequestRecycleBinModal';
import ServiceRequestAuthorityInfoModal from 'routes/Business/Job/JobDetail/ServiceRequestAuthorityInfoModal';

// 行按钮：给某个 Service Request 上传文档
// 用 AntD Upload 多选 + 拖拽弹窗（仿 SubJobDocumentTable/UploadDocument），不再用原生 <input type="file">
// parameter.key = service_request_id；sectionComponent 用于上传成功后刷新表格
export function uploadServiceRequestDocument({ parameter, sectionComponent } = {}) {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };
  const handleUploaded = () => {
    if (sectionComponent) {
      sectionComponent.getSectionDataByDefault();
    }
  };

  ReactDOM.render(
    <ServiceRequestUploadDocumentModal
      serviceRequestId={parameter.key}
      onClose={handleClose}
      onUploaded={handleUploaded}
    />,
    container
  );
}

// 行按钮：查看 / 添加某个 Service Request 的备注
// 用一个独立的 React 组件渲染弹窗（统一 antd 风格）；Add 不关闭，直接刷新列表
// parameter.key = service_request_id；sectionComponent 用于增删备注后刷新表格（更新 Notes 列最新备注）
export function showServiceRequestNote({ parameter, sectionComponent } = {}) {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };
  // 增/删备注后刷新外层 Service Request 表格，使 Notes 列显示最新备注
  const handleChanged = () => {
    if (sectionComponent) {
      sectionComponent.getSectionDataByDefault();
    }
  };

  ReactDOM.render(
    <ServiceRequestNoteModal
      serviceRequestId={parameter.key}
      onClose={handleClose}
      onChanged={handleChanged}
    />,
    container
  );
}

// 行按钮：打开某个 Service Request 的"回收站"——查看已删除文档 + 勾选还原
// parameter.key = service_request_id；还原成功后刷新表格让文档重新出现在 Files 列
export function showServiceRequestRecycleBin({ parameter, sectionComponent } = {}) {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };
  const handleRestored = () => {
    if (sectionComponent) {
      sectionComponent.getSectionDataByDefault();
    }
  };

  ReactDOM.render(
    <ServiceRequestRecycleBinModal
      serviceRequestId={parameter.key}
      onClose={handleClose}
      onRestored={handleRestored}
    />,
    container
  );
}

// 顶部按钮：Show Info —— 按当前 Job 的 LgaId / UtilityAuthorityId 弹窗展示
//   Council 的 Lodgement Info + Water Authority 的 Property Info Lodgement（富文本，链接可点击）
// 当前 Job 数据在 pageComponent.state.jobData（含 LgaId / UtilityAuthorityId / LgaName / UtilityAuthorityName）
export function showInfo({ pageComponent } = {}) {
  const jobData = pageComponent?.state?.jobData || {};
  const lgaId = jobData.LgaId;
  const utilityAuthorityId = jobData.UtilityAuthorityId;

  if (!lgaId && !utilityAuthorityId) {
    message.warning('This job has no linked Council or Water Authority.');
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);
  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) container.parentNode.removeChild(container);
  };

  ReactDOM.render(
    <ServiceRequestAuthorityInfoModal
      lgaId={lgaId}
      utilityAuthorityId={utilityAuthorityId}
      lgaName={jobData.LgaName}
      utilityAuthorityName={jobData.UtilityAuthorityName}
      onClose={handleClose}
    />,
    container
  );
}

// 顶部按钮：多选若干 SR 后批量改状态
// sectionComponent = DefaultSection；ServiceRequestTable 实例 = sectionComponent.child.current
// 选中的 ids 在 sectionComponent.child.current.state.selectedRowKeys
export function batchChangeServiceRequestStatus({ sectionComponent } = {}) {
  const table = sectionComponent?.child?.current;
  const selectedKeys = table?.state?.selectedRowKeys || [];
  if (selectedKeys.length === 0) {
    message.warning('Please select at least one service request.');
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);
  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) container.parentNode.removeChild(container);
  };
  const handleChanged = () => {
    if (sectionComponent) sectionComponent.getSectionDataByDefault();
    // 状态改完后清空已选，避免下次又误改
    table?.setState({ selectedRowKeys: [] });
  };

  ReactDOM.render(
    <ServiceRequestChangeStatusModal
      serviceRequestIds={selectedKeys}
      onClose={handleClose}
      onChanged={handleChanged}
    />,
    container
  );
}

// 顶/底部按钮：把所选 SR 当 LineItem 一键开 Invoice
// 复用既有 Invoice section 的 Add 流程（Common.addBySection），打开 Modal 后注入 InvoiceLineItemList
// 前提：当前 JobDetail 页面已挂 <section name="Invoice">
export function createInvoiceFromServiceRequests({ sectionComponent, pageComponent } = {}) {
  const table = sectionComponent?.child?.current;
  const selectedKeys = table?.state?.selectedRowKeys || [];
  const allData = table?.state?.data || [];
  const selectedRows = allData.filter(r => selectedKeys.indexOf(r.key) >= 0);
  if (selectedRows.length === 0) {
    message.warning('Please select at least one service request.');
    return;
  }

  // 找到当前页面的 Invoice section（页面上未挂时直接报错）
  const invoice = globalFunction.getSectionComponent(pageComponent, 'Invoice');
  if (!invoice) {
    message.error('Invoice section not found on this page.');
    return;
  }

  // 1) 沿用 Common.addBySection 打开 Invoice Create Modal（内部含 setTimeout 链：清字段、设默认 + 调 addInvoicePopUpRun）
  operationalFunction({
    functionName: 'addBySection',
    sectionName: 'Invoice',
    sectionComponent: invoice,
    pageComponent,
  });

  // 2) 等 Common.addBySection 的几层 setTimeout 都跑完后，再把 LineItem 注入表单
  setTimeout(() => {
    const formRef = invoice.customForm?.current?.formRef?.current;
    if (!formRef) {
      message.warning('Invoice form not ready, please retry.');
      return;
    }
    const lineItems = selectedRows.map((r, i) => ({
      index: i,
      // 描述用 ServiceRequestType 名字
      Description: r.ServiceRequestTypeName,
      Quantity: 1,
      // 服务费 + 机构费合并为一行金额（保留 2 位小数）
      UnitAmount: Math.round(((Number(r.ServiceCharge) || 0) + (Number(r.AuthorityCharge) || 0)) * 100) / 100,
      // 含税标记：服务费含税或机构费含税其一为 true 即勾选
      Tax: (r.ServiceChargeGst || r.AuthorityChargeGst) ? 1 : 0,
      NoDiscount: null,
      // 回写关联（如果后端 Invoice 落库时记下来可用）
      ServiceRequestId: r.key,
    }));
    formRef.setFieldsValue({ InvoiceLineItemList: lineItems });
    // setFieldsValue 不会触发字段级 onChange，必须手动调 InvoiceLineItemForm.calculateLineTotal 才能刷新底部 Subtotal/GST/Total
    // 与 InvoiceLineItemForm 内部 addLineItem / removeLineItem / accountCodeChange 用法完全一致
    setTimeout(() => {
      const lineItemForm = invoice.customForm?.current?.additionalFormRef?.current?.child?.current;
      if (lineItemForm && typeof lineItemForm.calculateLineTotal === 'function') {
        lineItemForm.calculateLineTotal();
      }
    }, config.setTimeoutMilliSecond);
  }, 500);   // Common.addBySection 内是 10ms+10ms 双层 setTimeout；这里 500ms 足够覆盖
}

// ───────────── Progress 列：点击快速切换状态 / 右键弹列表选状态（仿 JobChecklist） ─────────────

// 表格挂载时加载本公司全部 Service Request Status（带颜色 + 已按 Sort 排序），
// 供：左键快速切换时算"下一个"状态、右键菜单渲染彩色 badge
// 仿 JobChecklist.getChecklistStatusSets：在异步 .then 里 setState，此时父级 child ref 已挂好
export function loadServiceRequestStatusList({ sectionComponent, pageComponent } = {}) {
  httpClient
    .get('/Admin/ServiceRequestStatus/GetList')
    .then(({ data }) => {
      if (data.result) {
        sectionComponent.child.current?.setState({ statusList: data.data });
      } else {
        globalFunction.handleResultError(data, pageComponent);
      }
    })
    .catch(function (error) {
      globalFunction.handleError(error, pageComponent);
    });
}

// 左键点击 Progress badge：快速切到"下一个"状态（按 Sort 顺序循环）
// parameter = 行记录（parameter.key=ServiceRequestId, parameter.ServiceRequestStatusId=当前状态）
export function quickChangeServiceRequestStatus({ e, parameter, sectionComponent, pageComponent } = {}) {
  if (e) { e.stopPropagation(); }
  const table = sectionComponent?.child?.current;
  if (!table) { return false; }
  const statusList = table.state.statusList || [];
  if (statusList.length === 0) { return false; }   // 状态尚未加载
  let idx = statusList.findIndex(s => s.ServiceRequestStatusId === parameter.ServiceRequestStatusId);
  let next = statusList[(idx + 1) % statusList.length];
  if (!next || next.ServiceRequestStatusId === parameter.ServiceRequestStatusId) { return false; }
  applyServiceRequestStatus(table, parameter.key, next, pageComponent);
  return false;
}

// 右键 Progress badge：在鼠标处弹出状态列表菜单
export function showServiceRequestStatusMenu({ e, parameter, sectionComponent } = {}) {
  if (e) { e.preventDefault(); }
  sectionComponent?.child?.current?.setState({
    statusMenuEvent: e,
    statusMenuShow: true,
    statusMenuServiceRequestId: parameter.key,
  });
  return false;
}

// 从右键菜单选中某状态后调用（由 ServiceRequestStatusMenu 触发）
// parameter = { key: ServiceRequestId, status: <状态对象> }
export function changeServiceRequestStatus({ parameter, sectionComponent, pageComponent } = {}) {
  const table = sectionComponent?.child?.current;
  if (!table) { return; }
  applyServiceRequestStatus(table, parameter.key, parameter.status, pageComponent);
}

// 调后端把某 SR 改成指定状态，成功后本地乐观更新该行 + 关菜单（复用既有批量 ChangeStatus 接口，单元素数组）
function applyServiceRequestStatus(table, serviceRequestId, status, pageComponent) {
  setRowLoading(table, serviceRequestId, true);
  httpClient
    .post('/Business/ServiceRequest/ChangeStatus?serviceRequestStatusId=' + status.ServiceRequestStatusId, [serviceRequestId])
    .then(({ data }) => {
      if (data.result) {
        let newData = (table.state.data || []).map(r =>
          r.key === serviceRequestId
            ? {
              ...r,
              ServiceRequestStatusId: status.ServiceRequestStatusId,
              ServiceRequestStatusName: status.Name,
              ServiceRequestStatusBackgroundColor: status.BackgroundColor,
              Loading: false,
            }
            : r);
        table.setState({ data: newData, statusMenuShow: false });
      } else {
        setRowLoading(table, serviceRequestId, false);
        globalFunction.handleResultError(data, pageComponent);
      }
    })
    .catch(function (error) {
      setRowLoading(table, serviceRequestId, false);
      globalFunction.handleError(error, pageComponent);
    });
}

// 标记/取消某行 Loading（badge 渲染时 record.Loading===true 会显示转圈图标）
function setRowLoading(table, serviceRequestId, loading) {
  let newData = (table.state.data || []).map(r =>
    r.key === serviceRequestId ? { ...r, Loading: loading } : r);
  table.setState({ data: newData });
}

// Files 列里删除单个文件：DELETE /Business/ServiceRequest/DeleteDocument/{ServiceRequestDocumentId}
// parameter = { ServiceRequestDocumentId }
export function deleteServiceRequestDocument({ parameter, sectionComponent } = {}) {
  httpClient
    .delete('/Business/ServiceRequest/DeleteDocument/' + parameter.ServiceRequestDocumentId)
    .then(({ data }) => {
      if (data.result) {
        message.success('Document removed.');
        if (sectionComponent) {
          sectionComponent.getSectionDataByDefault();
        }
      } else {
        message.error(data.message || 'Delete failed.');
      }
    })
    .catch(() => message.error('Delete failed.'));
}

export function addServiceRequestPopUpRun({ parameter, sectionComponent, pageComponent, sectionName, uie } = {}) {
  console.log(sectionComponent, 'sectionComponent')
  let folderSelectedKeys = sectionComponent.customForm.current?.formRef.current?.getFieldValue();
  console.log(folderSelectedKeys)
  sectionComponent.customForm.current?.formRef.current?.setFieldValue('ServiceTypeId', 0);
}

export function serviceTypeOnChange({ sectionName, parameter, formComponent, sectionComponent, pageComponent, uie }) {
  console.log(111111);
  console.log(parameter, 'parameter---')
  console.log(sectionComponent.props.jobId, 'sectionComponent---')
  if (parameter != 0) {
    httpClient
      .get('/Admin/ServiceRequestType/GetServiceRequestDetails?id=' + parameter + '&jobid=' + sectionComponent.props.jobId)
      .then(({ data }) => {
        if (data.result) {
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('DaysLowerBound', data.data.DaysLowerBound);
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('DaysUpperBound', data.data.DaysUpperBound);
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('AuthorityCharge', data.data.DefaultAuthorityCharge);
          // Authority 设置可能不存在；ServiceRequest 的 GST 字段为非空 bool，未设置时按 false 提交。
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('AuthorityChargeGst', data.data.AuthorityChargeGST === true);
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('ServiceCharge', data.data.ServiceCharge);
          sectionComponent.customForm.current?.formRef.current?.setFieldValue('ServiceChargeGst', data.data.Tax === true);
        } else {
          globalFunction.handleResultError(data, pageComponent);
        }
      })
      .catch(function (error) {
        globalFunction.handleError(error, pageComponent);
      });
  } else {
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('DaysLowerBound', '');
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('DaysUpperBound', '');
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('AuthorityCharge', '');
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('AuthorityChargeGst', false);
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('ServiceCharge', '');
    sectionComponent.customForm.current?.formRef.current?.setFieldValue('ServiceChargeGst', false);
  }
}
// 底部 Email：把所选 Service Request 的附件带入 Job Detail 既有邮件弹窗。
export function sendEmail({ sectionComponent, pageComponent } = {}) {
  const table = sectionComponent?.child?.current;
  const selectedKeys = table?.state?.selectedRowKeys || [];
  const selectedRows = table?.state?.selectedRows?.length
    ? table.state.selectedRows
    : (table?.state?.data || []).filter(row => selectedKeys.some(key => String(key) === String(row.key)));

  if (selectedRows.length === 0) {
    message.warning('Please select at least one service request.');
    return;
  }

  // 同一文档可能关联多个服务项，按 DocumentId 去重，避免重复邮件附件。
  const documentMap = new Map();
  selectedRows.forEach(row => {
    (row.DocumentList || []).forEach(document => {
      if (document.DocumentId && !documentMap.has(document.DocumentId)) {
        documentMap.set(document.DocumentId, {
          key: document.DocumentId,
          DocumentId: document.DocumentId,
          Name: document.DocumentName,
          Size: document.DocumentSize || 0,
        });
      }
    });
  });
  const selectedDocumentList = Array.from(documentMap.values());

  // 复用原有邮件流程，确保主题、签名和发件人设置保持一致。
  // 保存当前表格实例，邮件成功回调可直接清空其勾选状态。
  pageComponent.serviceRequestEmailTable = table;
  openEmail({ parameter: { Email: '' }, pageComponent });
  pageComponent.setState({ selectedDocumentList, sendEmailInvoiceId: null, sendEmailSource: 'ServiceRequest' });
  setTimeout(() => {
    pageComponent.calculateSelectedDocumentTotalSize();
  }, config.setTimeoutMilliSecond);
}
